import { useTranslation } from "react-i18next";
import styles from "./ProfileInfoTab.module.css";
import LoadingSpinner from "../../UI/LoadingSpinner/LoadingSpinner";
import DefaultUserImage from "../../../assets/images/default-user.webp";

export default function ProfileInfoTab({
  allUsers,
  loadingUsers,
  usersError,
  fetchUsers,
  hasFetchedRef,
}) {
  const { t } = useTranslation("common");

  return (
    <div className={styles.tabContent}>
      <div className={styles.sectionHeader}>
        <div>
          <h2 className={styles.sectionTitle}>
            {t("userInfoPage.profileInfo.title")}
          </h2>
          <p className={styles.sectionDescription}>
            {t("userInfoPage.profileInfo.description")}
          </p>
        </div>
        <button
          type="button"
          className={styles.headerActionButton}
          onClick={() => {
            hasFetchedRef.current = false;
            fetchUsers();
          }}
        >
          {t("refresh")}
        </button>
      </div>

      {loadingUsers && <LoadingSpinner />}

      {usersError && (
        <div className={styles.errorContainer}>
          <p className={styles.errorMessage}>
            {t("error")}: {usersError}
          </p>
          <button
            type="button"
            className={styles.retryButton}
            onClick={() => {
              hasFetchedRef.current = false;
              fetchUsers();
            }}
          >
            {t("retry")}
          </button>
        </div>
      )}

      {!loadingUsers && !usersError && allUsers && allUsers.length > 0 && (
        <>
          <div
            className={`${styles.tableContainer} ${styles.tableContainerDesktopOnly}`}
          >
            <table className={styles.table}>
              <thead>
                <tr>
                  <th>{t("userInfoPage.profileInfo.tableHeaders.user")}</th>
                  <th>{t("userInfoPage.profileInfo.tableHeaders.email")}</th>
                  <th>{t("userInfoPage.profileInfo.tableHeaders.company")}</th>
                  <th>{t("userInfoPage.profileInfo.tableHeaders.phone")}</th>
                  <th>{t("userInfoPage.profileInfo.tableHeaders.address")}</th>
                </tr>
              </thead>
              <tbody>
                {allUsers.map((user) => (
                  <tr key={user._id}>
                    <td className={styles.userCell}>
                      <div className={styles.userInfo}>
                        <div className={styles.userAvatar}>
                          {user.profileImage ? (
                            <img
                              src={user.profileImage}
                              alt={user.name || "User"}
                              className={styles.profileImage}
                              onError={(e) => {
                                e.currentTarget.onerror = null;
                                e.currentTarget.src = DefaultUserImage;
                              }}
                            />
                          ) : (
                            (user.name || "U").charAt(0).toUpperCase()
                          )}
                        </div>
                        <div className={styles.userName}>
                          {user.name || "No Name"}
                        </div>
                      </div>
                    </td>
                    <td className={styles.emailCell}>{user.email}</td>
                    <td className={styles.companyCell}>
                      {user.company || "N/A"}
                    </td>
                    <td className={styles.phoneCell}>
                      {user.phoneNumber || "N/A"}
                    </td>
                    <td className={styles.locationCell}>
                      {user.address?.street &&
                      user.address?.city &&
                      user.address?.state &&
                      user.address?.country
                        ? `${user.address.street}, ${user.address.city}, ${user.address.state}, ${user.address.country}`
                        : user.address?.city && user.address?.country
                        ? `${user.address.city}, ${user.address.country}`
                        : user.address?.city || user.address?.country || "N/A"}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className={styles.mobileCardLayout}>
            <div className={styles.usersGrid}>
              {allUsers.map((user) => (
                <div key={user._id} className={styles.userCard}>
                  <div className={styles.userHeader}>
                    <div className={styles.userInfo}>
                      <div className={styles.userAvatar}>
                        {user.profileImage ? (
                          <img
                            src={user.profileImage}
                            alt={user.name || "User"}
                            className={styles.profileImage}
                            onError={(e) => {
                              e.currentTarget.onerror = null;
                              e.currentTarget.src = DefaultUserImage;
                            }}
                          />
                        ) : (
                          (user.name || "U").charAt(0).toUpperCase()
                        )}
                      </div>
                      <div className={styles.userDetails}>
                        <div className={styles.userName}>
                          {user.name || "No Name"}
                        </div>
                        <div className={styles.userEmail}>{user.email}</div>
                      </div>
                    </div>
                  </div>

                  <div className={styles.userMeta}>
                    <div className={styles.metaItem}>
                      <span className={styles.metaLabel}>
                        {t("userInfoPage.profileInfo.labels.company")}
                      </span>
                      <span className={styles.metaValue}>
                        {user.company || "N/A"}
                      </span>
                    </div>
                    <div className={styles.metaItem}>
                      <span className={styles.metaLabel}>
                        {t("userInfoPage.profileInfo.labels.phone")}
                      </span>
                      <span className={styles.metaValue}>
                        {user.phoneNumber || "N/A"}
                      </span>
                    </div>
                    <div className={styles.metaItem}>
                      <span className={styles.metaLabel}>
                        {t("userInfoPage.profileInfo.labels.address")}
                      </span>
                      <span className={styles.metaValue}>
                        {user.address?.street &&
                        user.address?.city &&
                        user.address?.state &&
                        user.address?.country
                          ? `${user.address.street}, ${user.address.city}, ${user.address.state}, ${user.address.country}`
                          : user.address?.city && user.address?.country
                          ? `${user.address.city}, ${user.address.country}`
                          : user.address?.city ||
                            user.address?.country ||
                            "N/A"}
                      </span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </>
      )}

      {!loadingUsers && !usersError && (!allUsers || allUsers.length === 0) && (
        <div className={styles.noUsers}>
          <p>{t("userInfoPage.profileInfo.noUsers")}</p>
          <button
            type="button"
            className={styles.noUsersButton}
            onClick={() => {
              hasFetchedRef.current = false;
              fetchUsers();
            }}
          >
            {t("userInfoPage.profileInfo.refreshUsers")}
          </button>
        </div>
      )}
    </div>
  );
}
